// src/elements/arrow.ts
import { Z } from "@/utils/constants";
import { log } from "@/utils/debug";

/** Orientation helper */
function isPortrait(): boolean {
  try {
    return matchMedia("(orientation: portrait)").matches;
  } catch {
    return window.innerHeight > window.innerWidth;
  }
}

function extraSafeBottom(): number {
  let bottomChrome = 0;
  const vv = window.visualViewport;
  if (vv) bottomChrome = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop));

  let safeAreaPx = 0;
  try {
    let probe = document.getElementById("__sc_safearea_probe__") as HTMLDivElement | null;
    if (!probe) {
      probe = document.createElement("div");
      probe.id = "__sc_safearea_probe__";
      Object.assign(probe.style, {
        position: "fixed",
        left: "0",
        right: "0",
        bottom: "0",
        height: "env(safe-area-inset-bottom, 0px)",
        width: "0",
        pointerEvents: "none",
        opacity: "0",
        zIndex: "-1",
      } as Partial<CSSStyleDeclaration>);
      document.body.appendChild(probe);
    }
    safeAreaPx = probe.getBoundingClientRect().height || 0;
  } catch {}
  return Math.max(bottomChrome, safeAreaPx);
}

/** Position an arrow button within its container */
function positionArrow(
  btn: HTMLElement,
  container: HTMLElement,
  dir: "up" | "down" | "left" | "right"
) {
  const cs = getComputedStyle(container);
  if (cs.position === "static") container.style.position = "relative";

  Object.assign(btn.style, { position: "absolute", inset: "auto" } as Partial<CSSStyleDeclaration>);

  const baseGap = 20;
  const topGap = 16;
  const bottomGap = 16;

  const viewportH =
    window.visualViewport?.height ||
    window.innerHeight ||
    document.documentElement.clientHeight;

  if (dir === "down") {
    btn.style.left = "50%";
    btn.style.transform = "translateX(-50%)";

    const isDesktop =
      typeof matchMedia !== "undefined" &&
      matchMedia("(pointer: fine) and (min-width: 1024px)").matches;

    if (isDesktop) {
      btn.style.bottom = "24px";
      btn.style.top = "auto";
      btn.style.right = "auto";
    } else {
      const safeBottom = extraSafeBottom();
      const viewportBasedGap = Math.round(Math.max(bottomGap, Math.min(40, viewportH * 0.025)));
      // Mobile browser chrome is not reported reliably enough to keep the
      // reel arrow safe near the bottom edge. Bias the control upward with a
      // conservative viewport floor so it clears the bottom bar even when
      // VisualViewport under-reports the obstruction.
      const minMobileBottom = Math.round(
        Math.max(72, Math.min(132, viewportH * 0.16))
      );
      const finalBottom = Math.max(safeBottom + viewportBasedGap, minMobileBottom);

      btn.style.bottom = `${finalBottom}px`;
      btn.style.top = "auto";
      btn.style.right = "auto";
    }
  } else if (dir === "up") {
    btn.style.left = "50%";
    btn.style.transform = "translateX(-50%)";
    btn.style.top = `${topGap}px`;
    btn.style.bottom = "auto";
    btn.style.right = "auto";
  } else if (dir === "left") {
    btn.style.top = "50%";
    btn.style.transform = "translateY(-50%)";
    btn.style.left = `${baseGap}px`;
    btn.style.right = "auto";
    btn.style.bottom = "auto";
  } else {
    btn.style.top = "50%";
    btn.style.transform = "translateY(-50%)";
    btn.style.right = `${baseGap}px`;
    btn.style.left = "auto";
    btn.style.bottom = "auto";
  }
}

/* ---------- Robust section resolution (WHY: wrappers break nextElementSibling) ---------- */

function resolveSectionRoot(node: Element | null): HTMLElement | null {
  return node ? (node.closest(".sc-section,[data-section-index],[id^='section-']") as HTMLElement | null) : null;
}

function allSections(): HTMLElement[] {
  return Array.from(
    document.querySelectorAll(".sc-section, [data-section-index], [id^='section-']")
  ) as HTMLElement[];
}

function parseSectionIndex(el: HTMLElement | null, all: HTMLElement[]): number {
  if (!el) return -1;
  const ds = (el as any).dataset?.sectionIndex;
  if (ds != null && ds !== "") {
    const n = Number(ds);
    if (Number.isFinite(n)) return n;
  }
  const m = (el.id || "").match(/section-(\d+)/);
  if (m) return Number(m[1]);
  return all.indexOf(el); // DOM order fallback
}

function resolveSiblingSection(
  direction: "next" | "prev",
  sectionLike: HTMLElement | null
): HTMLElement | null {
  const all = allSections();
  if (!all.length) return null;

  const root = resolveSectionRoot(sectionLike);
  let idx = parseSectionIndex(root, all);

  if (!Number.isFinite(idx) || idx < 0) {
    const near = all
      .map((s, i) => ({ i, top: s.getBoundingClientRect().top }))
      .sort((a, b) => Math.abs(a.top) - Math.abs(b.top))[0];
    idx = near?.i ?? 0;
  }

  const delta = direction === "next" ? 1 : -1;
  const nextIdx = idx + delta;
  if (nextIdx < 0 || nextIdx >= all.length) return null;
  return all[nextIdx] || null;
}

/** Hide up on first / down on last using index (not siblings) */
function hideIfEdge(btn: HTMLElement, container: HTMLElement, dir: "up" | "down" | "left" | "right") {
  const section = container.closest<HTMLElement>(".sc-section,[data-section-index],[id^='section-']");
  if (!section) return;
  const all = allSections();
  const idx = parseSectionIndex(section, all);
  const isFirst = idx <= 0;
  const isLast = idx === all.length - 1;
  if ((dir === "up" && isFirst) || (dir === "down" && isLast)) btn.style.display = "none";
  else btn.style.display = "";
}

/** Recheck all registered arrows and update their edge visibility */
export function recheckArrowEdges() {
  for (const a of _arrows) hideIfEdge(a.btn, a.container, a.dir);
}

// --- Global arrow registry for resize ---
const _arrows = new Set<{ btn: HTMLElement; container: HTMLElement; dir: "up"|"down"|"left"|"right" }>();
function registerArrow(btn: HTMLElement, container: HTMLElement, dir: "up"|"down"|"left"|"right") { _arrows.add({ btn, container, dir }); }
function positionAllArrows() { for (const a of _arrows) positionArrow(a.btn, a.container, a.dir); }

if (!(window as any).__scArrowResizeBound) {
  (window as any).__scArrowResizeBound = true;
  window.addEventListener("resize", positionAllArrows, { passive: true });
}

if (window.visualViewport && !(window as any).__scArrowVVResizeBound) {
  (window as any).__scArrowVVResizeBound = true;
  window.visualViewport.addEventListener("resize", positionAllArrows, { passive: true } as any);
  window.visualViewport.addEventListener("scroll", positionAllArrows, { passive: true } as any);
}

/** Ensure a scroll arrow button exists inside `container`. */
export function ensureScrollArrow(
  container: HTMLElement,
  zIndex: number,
  dir: "up" | "down" | "left" | "right" = "down"
) {
  let btn = container.querySelector<HTMLButtonElement>(`button[data-scroll-arrow="${dir}"]`);
  if (!btn) {
    btn = document.createElement("button");
    btn.type = "button";
    btn.dataset.scrollArrow = dir;
    btn.className = "scroll-arrow rounded-full border-0 flex items-center justify-center";

    btn.style.display = "none";
    btn.style.opacity = "0";
    btn.style.pointerEvents = "none";
    btn.style.visibility = "hidden";

    btn.setAttribute("role", "button");
    btn.setAttribute(
      "aria-label",
      dir === "down" ? "Scroll down" :
      dir === "up"   ? "Scroll up"   :
      dir === "left" ? "Scroll left" : "Scroll right"
    );

    Object.assign(btn.style, {
      width: "3rem",
      height: "3rem",
      cursor: "pointer",
      position: "relative",
      zIndex: String(zIndex ?? Z.HUD),
    });

    const img = document.createElement("span");
    img.textContent = dir === "down" ? "↓" : dir === "up" ? "↑" : dir === "left" ? "←" : "→";
    img.style.fontSize = "1.5rem";
    img.style.lineHeight = "1";
    btn.appendChild(img);

    btn.addEventListener("click", () => {
      const section = container.closest<HTMLElement>(".sc-section,[data-section-index],[id^='section-']");
      if (!section) return;

      let direction: "next" | "prev";
      if (dir === "down") direction = "next";
      else if (dir === "up") direction = "prev";
      else if (dir === "left") { direction = "prev"; log("[ARROW] Left clicked (prev)"); }
      else { direction = "next"; log("[ARROW] Right clicked (next)"); }

      const secLabel = section?.dataset.sectionIndex ?? section?.id ?? "(unknown)";
      log("[ARROW] Clicked", direction, "from section", secLabel);

      // Let scrollFromArrow handle event dispatch + navigation
      const resolved = resolveSiblingSection(direction, section);
      log("[ARROW] resolved target →", resolved?.id || resolved?.dataset?.sectionIndex);
      scrollFromArrow(direction, section);
    });

    container.appendChild(btn);
  }

  positionArrow(btn, container, dir);
  hideIfEdge(btn, container, dir);
  btn.style.zIndex = String(zIndex ?? Z.HUD);

  registerArrow(btn, container, dir);
  return btn;
}

/** Helper: attach arrows for a section based on its attrs. */
export function addSectionArrows(
  container: HTMLElement,
  arrowAttr: string | string[] | undefined,
  zIndex: number
) {
  if (!arrowAttr || String(arrowAttr).trim() === "") {
    log("[ARROWS] Skipping — no arrow attribute on section", container.id);
    return;
  }

  const valid: Array<"up" | "down" | "left" | "right"> = ["up", "down", "left", "right"];

  const raw = Array.isArray(arrowAttr) ? arrowAttr : (arrowAttr as string).split(/\s+/);

  const directions = raw
    .map(d => d.toLowerCase().trim())
    .filter((d): d is "up" | "down" | "left" | "right" => valid.includes(d as any));

  if (directions.length === 0) {
    log("[ARROWS] Ignored invalid arrow attribute(s) on section", container.id, "→", raw);
    return;
  }

  [...new Set(directions)].forEach(dir => ensureScrollArrow(container, zIndex, dir));
}

/** Fade in arrow after delay */
export function fadeInArrow(arrowEl: HTMLElement, delay = 1800) {
  if (!arrowEl) return;

  if (arrowEl.style.display === "flex" && arrowEl.style.visibility === "visible" && arrowEl.style.opacity === "1") {
    setTimeout(() => {
      try {
        arrowEl.style.pointerEvents = "auto";
        arrowEl.style.transition = "opacity 0.8s ease";
        void (arrowEl as any).offsetWidth;
        arrowEl.style.opacity = "1";
      } catch {}
    }, delay);
    return;
  }

  arrowEl.style.display = "none";
  arrowEl.style.opacity = "0";
  arrowEl.style.pointerEvents = "none";
  arrowEl.style.visibility = "hidden";

  setTimeout(() => {
    try {
      arrowEl.style.display = "flex";
      arrowEl.style.visibility = "visible";
      arrowEl.style.pointerEvents = "auto";
      arrowEl.style.transition = "opacity 0.8s ease";
      void (arrowEl as any).offsetWidth;
      arrowEl.style.opacity = "1";
    } catch {}
  }, delay);
}

export function fadeInSectionArrows(sectionEl: HTMLElement, delay = 1800) {
  if (!sectionEl) return;
  const arrows = sectionEl.querySelectorAll<HTMLElement>("[data-scroll-arrow]");
  arrows.forEach(arrow => fadeInArrow(arrow, delay));
}

export function hideSectionArrows(sectionEl: HTMLElement) {
  if (!sectionEl) return;
  const arrows = sectionEl.querySelectorAll<HTMLElement>("[data-scroll-arrow]");
  arrows.forEach(arrow => {
    try {
      arrow.style.transition = "opacity 0.2s ease";
      void (arrow as any).offsetWidth;
      arrow.style.opacity = "0";
      arrow.style.pointerEvents = "none";
      setTimeout(() => {
        try {
          arrow.style.display = "none";
          arrow.style.visibility = "hidden";
        } catch {}
      }, 200);
    } catch {}
  });
}

export function revealArrowsForSection(section: HTMLElement, hudType?: string) {
  if (!section) return;
  const existingArrows = section.querySelectorAll<HTMLElement>("[data-scroll-arrow]");
  if (existingArrows.length > 0) {
    fadeInSectionArrows(section, 1800);
    return;
  }

  const arrowAttr = section.getAttribute("data-arrow");
  if (!arrowAttr) return;

  addSectionArrows(section, arrowAttr as any, Z.HUD);
  fadeInSectionArrows(section, 1800);
}

export function initArrowNavigation() {
  if ((window as any).__scArrowNavBound) return;
  (window as any).__scArrowNavBound = true;

  window.addEventListener("sc:scroll", (e: Event) => {
    const { direction, section } =
      (e as CustomEvent<{ direction: "next" | "prev"; section: HTMLElement; raw?: "up"|"down"|"left"|"right" }>).detail;

    const target = resolveSiblingSection(direction, section);
    log("[ARROW][initArrowNavigation] sc:scroll", {
      direction,
      from: section?.id || section?.dataset?.sectionIndex,
      to: target?.id || target?.dataset?.sectionIndex
    });
  });
}

// --- Bridge: react to reel events to show/hide the bottom arrow ---
if (!(window as any).__scArrowBridgeBound) {
  (window as any).__scArrowBridgeBound = true;

  // Show a bottom arrow for the given section (reels call this after ended+delay)
  document.addEventListener("sc:arrow:show", (ev: Event) => {
    const { section, video } = (ev as CustomEvent<any>).detail || {};
    const sec = resolveSectionRoot(section || (video as HTMLElement) || null);
    if (!sec) return;

    // Ensure a DOWN arrow exists in this section and fade it in immediately
    const down = sec.querySelector<HTMLElement>('button[data-scroll-arrow="down"]')
      || ensureScrollArrow(sec, Z.HUD, "down");
    positionArrow(down, sec, "down");
    hideIfEdge(down, sec, "down");
    fadeInArrow(down, 0);
  }, { passive: true });

  // Hide any arrows in the section when reels are replayed/seeked/etc.
  document.addEventListener("sc:arrow:hide", (ev: Event) => {
    const { section, video } = (ev as CustomEvent<any>).detail || {};
    const sec = resolveSectionRoot(section || (video as HTMLElement) || null);
    if (!sec) return;
    hideSectionArrows(sec);
  }, { passive: true });
}

export function scrollFromArrow(direction: "next" | "prev", section: HTMLElement) {
  log("[ARROW][scrollFromArrow] invoked", {
    direction,
    section: section?.id || section?.dataset?.sectionIndex
  });

  const target = resolveSiblingSection(direction, section);

  // Dispatch observer event
  window.dispatchEvent(new CustomEvent("sc:scroll", {
    detail: { direction, section, raw: direction === "next" ? "down" : "up" }
  }));

  if (target instanceof HTMLElement) {
    try {
      target.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    } catch {
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY, behavior: "smooth" });
    }
    return;
  }

  log("[ARROW][scrollFromArrow] No resolvable sibling; nudging viewport");
  const delta = window.innerHeight * 0.85 * (direction === "next" ? 1 : -1);
  try { window.scrollBy({ top: delta, behavior: "smooth" }); }
  catch { window.scrollTo({ top: window.scrollY + delta }); }
}
